import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  creditParameters,
  type CreditParameterCatalog,
  type CreditParameterSet,
  type CreditParameterSpec,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';
import { classeDaAba, trilhoDeAbas } from '@/shared/ui/tabStyles';

import { CartaoDoParametro } from './CartaoDoParametro';
import { ComparacaoDeVersoes } from './ComparacaoDeVersoes';
import {
  AbrirRascunhoDialog,
  AtivarDialog,
  CancelarAgendamentoDialog,
} from './DialogosDeVersao';
import {
  MENSAGEM_APROVACAO_REPETIDA,
  MENSAGEM_AUTOAPROVACAO,
  MENSAGEM_SEGREGACAO,
  mensagemDoErro,
  type EscopoDoEditor,
} from './escopo';
import { PainelDeHeranca, PainelDeRegras } from './PaineisDeApoio';
import { BadgeDaSituacao, PainelDeVersoes } from './PainelDeVersoes';
import {
  avisoDaAtivacao,
  iguais,
  rotuloDaOrigem,
  situacaoDe,
  textoDasAprovacoes,
  type Resultado,
} from './valores';

type Aba = 'parametros' | 'versoes' | 'regras' | 'heranca';

/** Paginas de outros pacotes da missao, so por endereco: nada delas e importado. */
export const ROTA_DO_CONSUMO = '/configuracoes/credito/consumo';
export const ROTA_DO_SIMULADOR = '/configuracoes/credito/simulador';

const gruposDaVigidata = new Set(['vigidata', 'consumo_de_consultas']);

/** A versao como a listagem a traz: sem os valores. */
function semValores(versao: CreditParameterSet): CreditParameterSet {
  const { values: _valores, ...resto } = versao;
  return resto;
}

/**
 * Editor dos parametros do motor de credito, guiado pelo catalogo descritivo.
 *
 * Serve a empresa (Configuracoes > Analise de credito) e o padrao global (/admin),
 * pelo escopo. Mostra cada grupo funcional na ordem do catalogo, com o valor da versao
 * ativa e o do rascunho; edita o rascunho mandando so o que mudou; ativa com
 * justificativa e, onde a governanca existe, com inicio de vigencia; cancela o
 * agendamento da versao agendada; mostra as aprovacoes ja dadas quando a plataforma
 * exige mais de um aprovador; e compara quaisquer duas versoes. Quem decide tudo e o servidor: a tela so evita oferecer o
 * que ele vai recusar.
 */
export function EditorDeParametros({ escopo }: { escopo: EscopoDoEditor }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aba, setAba] = useState<Aba>('parametros');
  const [abrindo, setAbrindo] = useState(false);
  const [ativando, setAtivando] = useState(false);
  const [cancelando, setCancelando] = useState<CreditParameterSet | null>(null);
  const [vendoDiferenca, setVendoDiferenca] = useState(false);
  const [pendentes, setPendentes] = useState<Record<string, Resultado>>({});
  const [geracao, setGeracao] = useState(0);
  const [erroDeCampo, setErroDeCampo] = useState<{
    chave: string;
    mensagem: string;
  } | null>(null);

  const catalogo = useQuery({
    queryKey: [escopo.chave, 'catalogo'],
    queryFn: ({ signal }) => escopo.api.catalog(signal),
  });
  const versoes = useQuery({
    queryKey: [escopo.chave, 'versoes'],
    queryFn: ({ signal }) => escopo.api.list(signal),
    retry: false,
  });
  // A governanca (heranca, padrao global, vigencia futura) e construida em paralelo.
  // Sem a rota, a tela segue so com o que existe.
  const heranca = useQuery({
    queryKey: ['credito-parametros', 'heranca'],
    queryFn: ({ signal }) => creditParameters.inheritance(signal),
    enabled: escopo.tipo === 'empresa',
    retry: false,
  });
  const governanca = escopo.tipo === 'empresa' ? heranca.isSuccess : versoes.isSuccess;

  const lista = useMemo(() => versoes.data ?? [], [versoes.data]);
  const resumoDaAtiva = lista.find((v) => situacaoDe(v) === 'ativa');
  const resumoDoRascunho = lista.find((v) => v.status === 'rascunho');
  const agendadas = lista.filter((v) => situacaoDe(v) === 'agendada');

  const ativa = useQuery({
    queryKey: [escopo.chave, 'versao', resumoDaAtiva?.id, resumoDaAtiva?.values_hash],
    queryFn: ({ signal }) => escopo.api.get(resumoDaAtiva?.id ?? '', signal),
    enabled: resumoDaAtiva !== undefined,
  });
  const rascunho = useQuery({
    queryKey: [
      escopo.chave,
      'versao',
      resumoDoRascunho?.id,
      resumoDoRascunho?.values_hash,
    ],
    queryFn: ({ signal }) => escopo.api.get(resumoDoRascunho?.id ?? '', signal),
    enabled: resumoDoRascunho !== undefined,
  });

  const rotulos = useMemo(() => {
    const dados = catalogo.data;
    if (!dados) return {};
    const comRegras: Record<string, string> = { ...dados.value_labels };
    for (const regra of dados.rules) comRegras[regra.id] = `${regra.id} ${regra.name}`;
    return comRegras;
  }, [catalogo.data]);

  function atualizarVersao(versao: CreditParameterSet) {
    queryClient.setQueryData(
      [escopo.chave, 'versao', versao.id, versao.values_hash],
      versao,
    );
    queryClient.setQueryData<CreditParameterSet[]>(
      [escopo.chave, 'versoes'],
      (atuais) => atuais?.map((v) => (v.id === versao.id ? semValores(versao) : v)),
    );
    void queryClient.invalidateQueries({ queryKey: [escopo.chave, 'versoes'] });
  }

  function descartar() {
    setPendentes({});
    setErroDeCampo(null);
    setGeracao((g) => g + 1);
  }

  const salvar = useMutation({
    mutationFn: (valores: Record<string, unknown>) =>
      escopo.api.updateDraft(resumoDoRascunho?.id ?? '', { values: valores }),
    onSuccess: (versao, valores) => {
      atualizarVersao(versao);
      descartar();
      const total = Object.keys(valores).length;
      toast.success(
        total === 1
          ? 'Rascunho salvo: 1 parametro alterado.'
          : `Rascunho salvo: ${total} parametros alterados.`,
      );
    },
    onError: (erro) => {
      const campo = erro instanceof ApiError ? erro.details?.field : undefined;
      if (typeof campo === 'string' && campo.startsWith('values.')) {
        setErroDeCampo({
          chave: campo.slice('values.'.length),
          mensagem: erro.message,
        });
      }
    },
  });

  function aoMudar(chave: string, resultado: Resultado) {
    setErroDeCampo(null);
    setPendentes((atuais) => {
      const gravado = rascunho.data?.values?.[chave] ?? null;
      const proximos = { ...atuais };
      if (resultado.ok && iguais(resultado.valor, gravado)) {
        delete proximos[chave];
      } else {
        proximos[chave] = resultado;
      }
      return proximos;
    });
  }

  const alterados = Object.entries(pendentes);
  const comErro = alterados.filter(([, r]) => !r.ok).length;

  function enviar() {
    if (comErro > 0) return;
    const valores: Record<string, unknown> = {};
    for (const [chave, resultado] of alterados) {
      if (resultado.ok) valores[chave] = resultado.valor;
    }
    salvar.mutate(valores);
  }

  if (catalogo.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar o catalogo dos parametros">
        {mensagemDoErro(catalogo.error)}
      </Alert>
    );
  }
  if (catalogo.isPending || versoes.isPending) return <SkeletonText lines={8} />;
  if (versoes.isError) {
    return escopo.tipo === 'plataforma' &&
      versoes.error instanceof ApiError &&
      versoes.error.status === 404 ? (
      <Alert tone="info" title="Padrao global ainda nao disponivel">
        Esta instalacao ainda nao tem as rotas do padrao global da plataforma. O
        catalogo dos parametros ja esta disponivel; a edicao do padrao global aparece
        aqui quando as rotas existirem.
      </Alert>
    ) : (
      <Alert tone="danger" title="Nao foi possivel carregar as versoes">
        {mensagemDoErro(versoes.error)}
      </Alert>
    );
  }

  const dados: CreditParameterCatalog = catalogo.data;
  const editavel = escopo.podeEditar && resumoDoRascunho !== undefined;
  // Os parametros so aparecem com os valores das duas versoes lidos: um editor montado
  // antes nasceria do rascunho vazio e seria trocado na chegada dos valores.
  const carregandoValores =
    (resumoDaAtiva !== undefined && ativa.isPending) ||
    (resumoDoRascunho !== undefined && rascunho.isPending);
  const erroDosValores = ativa.error ?? rascunho.error;
  const abas: { chave: Aba; rotulo: string }[] = [
    { chave: 'parametros', rotulo: 'Parametros' },
    { chave: 'versoes', rotulo: 'Versoes e comparacao' },
    { chave: 'regras', rotulo: 'Regras e pendencias' },
    ...(escopo.tipo === 'empresa'
      ? [{ chave: 'heranca' as const, rotulo: 'Heranca' }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      <ResumoDasVersoes
        escopo={escopo}
        ativa={resumoDaAtiva}
        rascunho={resumoDoRascunho}
        agendadas={agendadas}
        alteracoesPendentes={alterados.length}
        aoAbrir={() => setAbrindo(true)}
        aoAtivar={() => setAtivando(true)}
        aoCancelarAgendamento={setCancelando}
        aoVerDiferenca={() => setVendoDiferenca(true)}
      />

      <div role="tablist" aria-label="Analise de credito" className={trilhoDeAbas}>
        {abas.map((item) => (
          <button
            key={item.chave}
            type="button"
            role="tab"
            aria-selected={aba === item.chave}
            onClick={() => setAba(item.chave)}
            className={aba === item.chave ? classeDaAba(true) : classeDaAba(false)}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      {aba === 'parametros' ? (
        <div className="flex flex-col gap-5">
          {alterados.length > 0 ? (
            <div
              role="status"
              className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning-500/30 bg-warning-50 px-4 py-3 text-sm"
            >
              <span className="text-ink-800">
                {alterados.length === 1
                  ? '1 parametro alterado e nao salvo.'
                  : `${alterados.length} parametros alterados e nao salvos.`}
                {comErro > 0 ? ' Corrija os campos marcados antes de salvar.' : ''}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={descartar}>
                  Descartar
                </Button>
                <Button
                  size="sm"
                  loading={salvar.isPending}
                  disabled={comErro > 0}
                  onClick={enviar}
                >
                  Salvar alteracoes
                </Button>
              </div>
            </div>
          ) : null}
          {salvar.isError ? (
            <Alert tone="danger" title="Nao foi possivel salvar o rascunho">
              {mensagemDoErro(salvar.error)}
            </Alert>
          ) : null}

          {erroDosValores ? (
            <Alert
              tone="danger"
              title="Nao foi possivel carregar os valores das versoes"
            >
              {mensagemDoErro(erroDosValores)}
            </Alert>
          ) : null}
          {carregandoValores ? <SkeletonText lines={8} /> : null}

          {(carregandoValores ? [] : dados.groups).map((grupo) => {
            const doGrupo = dados.parameters.filter((spec) => spec.group === grupo.key);
            return (
              <Card key={grupo.key}>
                <CardHeader
                  title={grupo.label}
                  description={grupo.description}
                  action={
                    escopo.tipo === 'empresa' && gruposDaVigidata.has(grupo.key) ? (
                      <div className="flex flex-wrap gap-3 text-xs font-medium">
                        <Link
                          to={ROTA_DO_CONSUMO}
                          className="text-brand-600 hover:underline"
                        >
                          Consumo de consultas
                        </Link>
                        <Link
                          to={ROTA_DO_SIMULADOR}
                          className="text-brand-600 hover:underline"
                        >
                          Simulador
                        </Link>
                      </div>
                    ) : undefined
                  }
                />
                <CardBody>
                  {doGrupo.length === 0 ? (
                    <div className="flex flex-col items-start gap-2 text-sm text-ink-600">
                      <p>
                        Nenhuma regra documentada para parametrizar neste grupo. O valor
                        so entra quando a fonte for aprovada; ate la, nada e inventado.
                      </p>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setAba('regras')}
                      >
                        Ver as pendencias de regra
                      </Button>
                    </div>
                  ) : (
                    <div
                      key={`${rascunho.data?.id ?? 'sem'}:${rascunho.data?.values_hash ?? ''}:${geracao}`}
                      className="flex flex-col"
                    >
                      {doGrupo.map((spec: CreditParameterSpec) => (
                        <CartaoDoParametro
                          key={spec.key}
                          spec={spec}
                          ativa={resumoDaAtiva ? ativa.data : undefined}
                          rascunho={resumoDoRascunho ? rascunho.data : undefined}
                          editavel={editavel}
                          rotulos={rotulos}
                          regras={dados.rules}
                          pendente={pendentes[spec.key]}
                          erroDoServidor={
                            erroDeCampo?.chave === spec.key
                              ? erroDeCampo.mensagem
                              : undefined
                          }
                          onChange={(resultado) => aoMudar(spec.key, resultado)}
                        />
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      ) : null}

      {aba === 'versoes' ? (
        <PainelDeVersoes
          escopo={escopo}
          versoes={lista}
          catalogo={dados}
          rotulos={rotulos}
        />
      ) : null}
      {aba === 'regras' ? (
        <PainelDeRegras
          regras={dados.rules}
          homologadasNaAtiva={resumoDaAtiva?.homologated_rules ?? []}
          temAtiva={resumoDaAtiva !== undefined}
        />
      ) : null}
      {aba === 'heranca' ? <PainelDeHeranca podeEditar={escopo.podeEditar} /> : null}

      <AbrirRascunhoDialog
        aberto={abrindo}
        aoFechar={() => setAbrindo(false)}
        escopo={escopo}
        temAtiva={resumoDaAtiva !== undefined}
        padraoGlobalDisponivel={escopo.tipo === 'empresa' && governanca}
        aoAbrir={(versao) => {
          setAbrindo(false);
          atualizarVersao(versao);
          setAba('parametros');
          toast.success(`Rascunho da versao ${versao.version} aberto.`);
        }}
      />
      {resumoDoRascunho ? (
        <AtivarDialog
          aberto={ativando}
          aoFechar={() => setAtivando(false)}
          escopo={escopo}
          rascunho={resumoDoRascunho}
          vigenciaDisponivel={governanca}
          aoAtivar={(versao) => {
            setAtivando(false);
            atualizarVersao(versao);
            toast.success(avisoDaAtivacao(versao));
          }}
        />
      ) : null}
      <CancelarAgendamentoDialog
        versao={cancelando}
        aoFechar={() => setCancelando(null)}
        escopo={escopo}
        aoCancelar={(versao) => {
          setCancelando(null);
          atualizarVersao(versao);
          toast.success(
            `Agendamento da versao ${versao.version} cancelado. A versao ativa continua valendo.`,
          );
        }}
      />
      <Modal
        open={
          vendoDiferenca &&
          resumoDoRascunho !== undefined &&
          resumoDaAtiva !== undefined
        }
        onClose={() => setVendoDiferenca(false)}
        size="lg"
        title="Diferenca do rascunho para a versao ativa"
        description="Compara o rascunho salvo. Alteracoes ainda nao salvas nao entram."
      >
        {resumoDoRascunho && resumoDaAtiva ? (
          <ComparacaoDeVersoes
            escopo={escopo}
            catalogo={dados}
            rotulos={rotulos}
            baseId={resumoDoRascunho.id}
            outraId={resumoDaAtiva.id}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function ResumoDasVersoes({
  escopo,
  ativa,
  rascunho,
  agendadas,
  alteracoesPendentes,
  aoAbrir,
  aoAtivar,
  aoCancelarAgendamento,
  aoVerDiferenca,
}: {
  escopo: EscopoDoEditor;
  ativa: CreditParameterSet | undefined;
  rascunho: CreditParameterSet | undefined;
  agendadas: CreditParameterSet[];
  alteracoesPendentes: number;
  aoAbrir: () => void;
  aoAtivar: () => void;
  aoCancelarAgendamento: (versao: CreditParameterSet) => void;
  aoVerDiferenca: () => void;
}) {
  const autor = rascunho?.requester_is_author === true;
  const autoaprovacao = autor && rascunho?.requester_may_self_approve === true;
  // Autor sem a liberacao nominal nao ativa (segregacao de funcoes).
  const barradoPorAutoria = autor && !autoaprovacao;
  const jaAprovou = rascunho?.requester_has_approved === true;
  const aprovacoes = rascunho?.approvals ?? [];
  return (
    <Card>
      <CardHeader
        title={
          escopo.tipo === 'empresa'
            ? 'Versoes de parametros'
            : 'Versoes do padrao global'
        }
        description="So o rascunho muda. A versao ativa e a que o motor usa agora."
        action={
          <div className="flex flex-wrap gap-2">
            {escopo.podeEditar && !rascunho ? (
              <Button size="sm" onClick={aoAbrir}>
                Abrir rascunho
              </Button>
            ) : null}
            {rascunho && ativa ? (
              <Button size="sm" variant="secondary" onClick={aoVerDiferenca}>
                Diferenca para a ativa
              </Button>
            ) : null}
            {escopo.podeAtivar && rascunho ? (
              <Button
                size="sm"
                disabled={barradoPorAutoria || jaAprovou || alteracoesPendentes > 0}
                onClick={aoAtivar}
              >
                Ativar rascunho
              </Button>
            ) : null}
          </div>
        }
      />
      <CardBody className="flex flex-col gap-3 text-sm">
        {ativa ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink-900">{`Versao ${ativa.version}`}</span>
            <BadgeDaSituacao versao={ativa} />
            <span className="text-ink-600">
              {`Em vigor desde ${formatDateTime(ativa.effective_from ?? ativa.activated_at)}, ativada por ${ativa.activated_label || 'sistema'}.`}
            </span>
          </div>
        ) : (
          <Alert tone="warning" title="Nenhuma versao ativa">
            Sem versao ativa, o motor roda inteiro em sombra: registra o que teria
            decidido e nada decide (PM-01).
          </Alert>
        )}
        {agendadas.map((versao) => (
          <div key={versao.id} className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink-900">{`Versao ${versao.version}`}</span>
            <BadgeDaSituacao versao={versao} />
            <span className="text-ink-600">
              {`Entra em vigor em ${formatDateTime(versao.effective_from)}. Ate la, vale a versao ativa.`}
            </span>
            {escopo.podeAtivar ? (
              <Button
                size="sm"
                variant="danger"
                aria-label={`Cancelar o agendamento da versao ${versao.version}`}
                onClick={() => aoCancelarAgendamento(versao)}
              >
                Cancelar agendamento
              </Button>
            ) : null}
          </div>
        ))}
        {rascunho ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink-900">{`Versao ${rascunho.version}`}</span>
            <BadgeDaSituacao versao={rascunho} />
            <span className="text-ink-600">
              {`${rotuloDaOrigem(rascunho)}. Aberto por ${rascunho.created_label || 'sistema'} em ${formatDateTime(rascunho.created_at)}.`}
            </span>
          </div>
        ) : (
          <p className="text-ink-600">Nenhum rascunho aberto.</p>
        )}
        {rascunho && aprovacoes.length > 0 ? (
          <div className="flex flex-col gap-1 rounded-md border border-line bg-surface-sunken px-3 py-2">
            <span className="text-xs font-semibold text-ink-800">
              {`${textoDasAprovacoes(aprovacoes.length)}; a versao entra em vigor com a aprovacao de outra pessoa.`}
            </span>
            <ul className="flex flex-col gap-0.5 text-xs text-ink-600">
              {aprovacoes.map((aprovacao) => (
                <li key={`${aprovacao.label}:${aprovacao.at}`}>
                  {`${aprovacao.label || 'Sem nome'} em ${formatDateTime(aprovacao.at)}${
                    aprovacao.requested_effective_from
                      ? `, vigencia pedida para ${formatDateTime(aprovacao.requested_effective_from)}`
                      : ''
                  }${aprovacao.autoaprovacao ? ' (autoaprovacao)' : ''}.`}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {escopo.podeAtivar && jaAprovou && !barradoPorAutoria ? (
          <Alert tone="info" title="Aprovacao ja registrada">
            {MENSAGEM_APROVACAO_REPETIDA}
          </Alert>
        ) : null}
        {escopo.podeAtivar && barradoPorAutoria ? (
          <Alert tone="info" title="Ativacao por outra pessoa">
            {MENSAGEM_SEGREGACAO}
          </Alert>
        ) : null}
        {escopo.podeAtivar && autoaprovacao && !jaAprovou ? (
          <Alert tone="warning" title="Autoaprovacao liberada">
            {MENSAGEM_AUTOAPROVACAO}
          </Alert>
        ) : null}
        {escopo.podeAtivar &&
        rascunho &&
        !barradoPorAutoria &&
        !jaAprovou &&
        alteracoesPendentes > 0 ? (
          <p className="text-xs text-ink-500">
            Salve ou descarte as alteracoes antes de ativar: a ativacao vale para o
            rascunho salvo.
          </p>
        ) : null}
        {!escopo.podeEditar ? (
          <Alert tone="info" title="Somente leitura">
            O seu perfil nao inclui a edicao dos parametros de credito.
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}
